import { Autocomplete, Box, TextField, IconButton } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SaveCancelButtons from "components/Buttons/SaveCancelButtons";
import { Axios } from "api/axios";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import Colors from "Constants/Color";
import { api } from "helpers/api_url";
import { useEffect, useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import QuotationBagSelectionModal from "./QuotationBagSelectionModal";
import QuotationViewBagsModal from "./QuotationViewBagsModal";
import { EditButton } from "components/Buttons/EditButton";
import { DeleteButton } from "components/Buttons/DeleteButton";
import { autocompleteDropdownStyles, tableStyles } from "Constants/Style";
import SubmitButton from "components/Buttons/SubmitButton";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";

interface CategoryOption {
  category_id: string;
  category_name: string;
}

interface SubCategoryOption {
  sub_category_id: string;
  sub_category_name: string;
}

interface ItemOption {
  item_id: string;
  item_name: string;
  item_code: string;
  unit?: {
    unit_name: string;
  };
  selling_price?: string | number;
  hsn_code?: {
    gst_percent?: string | number;
  };
}

interface GstOption {
  gst_percent: string | number;
}

interface FormValues {
  organisation: any;
  department: any;
  team: any;
  user: any;
}

const QuotationItemTable = ({
  formik,
  mainFormik,
  editingItemId,
  setEditingItemId,
  bagQuantityValidation,
  setBagQuantityValidation,
  warehouseId,
  customSubmitHandler,
}: {
  formik: any;
  mainFormik?: any;
  editingItemId?: number | null;
  setEditingItemId?: (id: number | null) => void;
  bagQuantityValidation?: Array<{
    inventory_item_id: string | number;
    available_quantity: number;
    issue_quantity: number;
  }>;
  setBagQuantityValidation?: (
    bags: Array<{
      inventory_item_id: string | number;
      available_quantity: number;
      issue_quantity: number;
    }>
  ) => void;
  warehouseId?: string | number;
  customSubmitHandler?: () => Promise<void>;
}) => {
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState<
    SubCategoryOption[]
  >([]);
  const [itemOptions, setItemOptions] = useState<ItemOption[]>([]);
  const [gstOptions, setGstOptions] = useState<GstOption[]>([]);
  const [showBagModal, setShowBagModal] = useState(false);
  const [selectedBags, setSelectedBags] = useState<any[]>([]);
  const [showViewBagsModal, setShowViewBagsModal] = useState(false);
  const [viewBagsForItem, setViewBagsForItem] = useState<any[]>([]);
  const [editingRowData, setEditingRowData] = useState<any | null>(null);

  useEffect(() => {
    Axios.get(`${api?.master?.category?.dropdown}`)
      .then((result) => {
        setCategoryOptions(result?.data?.data || []);
      })
      .catch((err) => {
        toast.error("Failed to fetch category dropdown");
      });
    Axios.get(`${api?.master?.gst?.dropdown}`)
      .then((result) => {
        setGstOptions(result?.data?.data || []);
      })
      .catch((err) => {
        toast.error("Failed to fetch GST dropdown");
      });
  }, []);

  // Auto-calculate Amount when Quantity or Rate changes
  useEffect(() => {
    console.log("formik.values in QuotationItemTable: ", formik.values);
    const quantity = parseFloat(formik.values.quantity) || 0;
    const rate = parseFloat(formik.values.rate) || 0;
    const amount = quantity * rate;
    formik.setFieldValue("amount", amount.toFixed(2));
  }, [formik.values.quantity, formik.values.rate]);

  // Auto-calculate Discount Amount when Amount, Discount, or Discount Type changes
  useEffect(() => {
    const amount = parseFloat(formik.values.amount) || 0;
    const discount = parseFloat(formik.values.discount) || 0;
    const discountType = formik.values.discount_type || "%";
    console.log("amount in QuotationItemTable: ", amount);
    console.log("discount in QuotationItemTable: ", discount);
    console.log("discountType in QuotationItemTable: ", discountType);
    let discountAmount = 0;
    if (discountType === "%") {
      discountAmount = (amount * discount) / 100;
    } else if (discountType === "₹") {
      discountAmount = discount;
    }
    console.log("discountAmount in QuotationItemTable: ", discountAmount);
    formik.setFieldValue("discount_amount", discountAmount.toFixed(2));
  }, [
    formik.values.amount,
    formik.values.discount,
    formik.values.discount_type,
  ]);

  // Auto-calculate GST Amount and Total Amount when Net Amount or GST changes
  useEffect(() => {
    const amount = parseFloat(formik.values.amount) || 0;
    const discountAmount = parseFloat(formik.values.discount_amount) || 0;
    const netAmount = amount - discountAmount;
    const gstPercent = formik.values.gst?.gst_percent
      ? parseFloat(formik.values.gst.gst_percent)
      : 0;

    const gstAmount = (netAmount * gstPercent) / 100;
    const totalAmount = netAmount + gstAmount;

    formik.setFieldValue("gst_amount", gstAmount.toFixed(2));
    formik.setFieldValue("total_amount", totalAmount.toFixed(2));
  }, [formik.values.amount, formik.values.discount_amount, formik.values.gst]);

  const fetchSubCategoryDropdownByCategory = (category_id: string) => {
    Axios.get(
      `${api?.master?.sub_category?.fetchSubCategoryDropdownByCategoryId}/${category_id}`
    )
      .then((result) => {
        setSubCategoryOptions(result?.data?.data || []);
      })
      .catch((err) => {
        toast.error("Failed to fetch sub category dropdown");
      });
  };
  const fetchItemOptions = (category_id: string, sub_category_id: string) => {
    Axios.get(
      `/master/item/dropdown/category/${category_id}/sub-category/${sub_category_id}`
    )
      .then((result) => {
        setItemOptions(result?.data?.data || []);
      })
      .catch((err) => {
        toast.error("Failed to fetch sub category dropdown");
      });
  };

  const handleEditItem = async (item: any) => {
    // Ensure item has an id for editing
    const itemId = item.id || item.quotation_item_id;
    if (setEditingItemId && itemId) {
      setEditingItemId(itemId);
    }

    setEditingRowData({ ...item, id: itemId });

    // Reset add form to prevent autofilling when editing
    formik.resetForm();
    setSelectedBags([]);

    // Load subcategories if category exists
    if (item.category) {
      await fetchSubCategoryDropdownByCategory(item.category.category_id);
    }

    // Load items if subcategory exists
    if (item.sub_category && item.category && item.category.category_id) {
      await fetchItemOptions(
        item.category.category_id,
        item.sub_category.sub_category_id
      );
    }
  };

  const handleSaveEdit = () => {
    if (!editingRowData || !editingItemId) return;

    // Validate that at least one bag is selected
    if (
      !editingRowData.bags ||
      !Array.isArray(editingRowData.bags) ||
      editingRowData.bags.length === 0
    ) {
      toast.error("At least one bag must be selected for this item");
      return;
    }

    const currentItems = mainFormik.values.items || [];
    const updatedItems = currentItems.map((item: any) => {
      // Compare IDs using string conversion to handle type mismatches
      const itemId = String(item.id || item.quotation_item_id || "");
      const editingId = String(editingItemId || "");

      if (itemId === editingId) {
        // Preserve critical fields from original item that might not be in editingRowData
        // Only preserve if they exist in the original item (even if null)
        const preservedFields: any = {
          id: item.id || item.quotation_item_id || editingItemId,
        };
        console.log("preservedFields in handleSaveEdit: ", preservedFields);
        // Preserve these fields only if they exist in the original item (including null values)
        if ("quotation_item_id" in item)
          preservedFields.quotation_item_id = item.quotation_item_id;
        if ("hsn_code_id" in item)
          preservedFields.hsn_code_id = item.hsn_code_id;
        if ("hsn_code_name" in item)
          preservedFields.hsn_code_name = item.hsn_code_name;
        if ("unit_id" in item) preservedFields.unit_id = item.unit_id;
        if ("unit_name" in item) preservedFields.unit_name = item.unit_name;
        if ("unit_symbol" in item)
          preservedFields.unit_symbol = item.unit_symbol;
        if ("item_id" in item) preservedFields.item_id = item.item_id;
        if ("item_name" in item) preservedFields.item_name = item.item_name;
        if ("item_code" in item) preservedFields.item_code = item.item_code;
        if ("category_id" in item)
          preservedFields.category_id = item.category_id;
        if ("category_name" in item)
          preservedFields.category_name = item.category_name;
        if ("sub_category_id" in item)
          preservedFields.sub_category_id = item.sub_category_id;
        if ("sub_category_name" in item)
          preservedFields.sub_category_name = item.sub_category_name;
        if ("gst_id" in item) preservedFields.gst_id = item.gst_id;
        if ("gst_percent" in item)
          preservedFields.gst_percent = item.gst_percent;

        // Merge: original item (base) -> edited data (overrides) -> preserved fields (ensures critical fields are kept)
        return {
          ...item,
          ...editingRowData,
          ...preservedFields,
        };
      }
      return item;
    });

    // Update items and reset editing state together
    mainFormik.setFieldValue("items", updatedItems, false);

    // Reset editing state immediately after updating items
    // This ensures isEditing becomes false for all rows
    if (setEditingItemId) {
      setEditingItemId(null);
    }
    setEditingRowData(null);

    // Reset form to clear any residual state
    formik.resetForm();
    setSelectedBags([]);

    toast.success("Item updated successfully");
  };

  const handleCancelEdit = () => {
    if (setEditingItemId) {
      setEditingItemId(null);
    }
    setEditingRowData(null);
    formik.resetForm();
    setSelectedBags([]);
  };

  const handleEditFieldChange = (key: string, value: any) => {
    if (!editingRowData) return;
    const updated = { ...editingRowData, [key]: value };

    // Note: Bags are NOT cleared when quantity changes in edit mode
    // This allows users to edit quantity without losing their bag selections
    // If quantity needs to be adjusted, users can modify bags through the bag selection modal

    // Recalculate amount if quantity or rate changes
    if (key === "quantity" || key === "rate") {
      const quantity = parseFloat(String(updated.quantity)) || 0;
      const rate = parseFloat(String(updated.rate)) || 0;
      updated.amount = (quantity * rate).toFixed(2);
    }

    // Recalculate discount amount if amount, discount, or discount_type changes
    if (key === "amount" || key === "discount" || key === "discount_type") {
      const amount = parseFloat(String(updated.amount)) || 0;
      const discount = parseFloat(String(updated.discount)) || 0;
      const discountType = updated.discount_type || "%";
      let discountAmount = 0;
      if (discountType === "%") {
        discountAmount = (amount * discount) / 100;
      } else if (discountType === "₹") {
        discountAmount = discount;
      }
      updated.discount_amount = discountAmount.toFixed(2);
    }

    // Recalculate GST amount and total amount
    // This should run if amount, discount, discount_type, discount_amount, or gst changes
    // Always recalculate to ensure values are up to date
    const amount = parseFloat(String(updated.amount)) || 0;
    const discountAmount = parseFloat(String(updated.discount_amount)) || 0;
    const netAmount = amount - discountAmount;
    const gstPercent = updated.gst?.gst_percent
      ? parseFloat(updated.gst.gst_percent)
      : 0;
    const gstAmount = (netAmount * gstPercent) / 100;
    const totalAmount = netAmount + gstAmount;
    updated.gst_amount = gstAmount.toFixed(2);
    updated.total_amount = totalAmount.toFixed(2);

    setEditingRowData(updated);
  };

  return (
    <>
      <style>
        {`
                    .sticky-column {
                        position: relative;
                    }
                    .sticky-column::before {
                        content: '';
                        position: absolute;
                        left: 0;
                        top: 0;
                        bottom: 0;
                        width: 1px;
                        background-color: #dee2e6;
                        z-index: 12;
                    }
                    thead .sticky-column::before {
                        z-index: 13;
                    }
                `}
      </style>
      <div className="mb-4">
        <Table hover responsive className="align-middle text-center">
          <thead>
            <tr>
              <th style={{ ...tableStyles.headerAlt, minWidth: "80px" }}>
                Sr No.
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Category
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Sub Category
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Item Name
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Item Code
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Unit
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Quantity
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Bags
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Rate
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Amount
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "250px" }}>
                Discount
              </th>
              {/* <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>Discount Type</th> */}
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Discount Amount
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                GST%
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                GST Amount
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Total Amount
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Remarks
              </th>
              <th
                className="sticky-column"
                style={{
                  ...tableStyles.headerAlt,
                  ...tableStyles.actionHeader,
                  minWidth: "100px",
                }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Input Row */}
            <tr>
              <td style={tableStyles.cell}></td>
              <td style={tableStyles.cell}>
                <Autocomplete
                  size="small"
                  options={categoryOptions || []}
                  autoHighlight
                  value={formik?.values?.category}
                  onChange={(_, val) => {
                    formik.setFieldValue("category", val);
                    if (val) {
                      fetchSubCategoryDropdownByCategory(val?.category_id);
                    }
                    setSubCategoryOptions([]);
                    setItemOptions([]);
                    formik.setFieldValue("sub_category", null);
                    formik.setFieldValue("item", null);
                    formik.setFieldValue("item_code", "");
                    formik.setFieldValue("unit", "");
                    formik.setFieldValue("rate", "");
                    formik.setFieldValue("gst", null);
                    formik.setFieldValue("quantity", "");
                    formik.setFieldValue("bags", []);
                    setSelectedBags([]);
                    formik.setFieldValue("discount", "");
                    formik.setFieldValue("discount_type", "%");
                    formik.setFieldValue("discount_amount", "");
                    formik.setFieldValue("gst_amount", "");
                    formik.setFieldValue("total_amount", "");
                    formik.setFieldValue("remark", "");
                    // Cancel edit mode when category changes
                    if (editingItemId !== null && setEditingItemId) {
                      setEditingItemId(null);
                      setEditingRowData(null);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("category", true)}
                  getOptionLabel={(option) =>
                    option ? option?.category_name : ""
                  }
                  renderOption={(props, option: any) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt?.category_name || ""
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Category"
                      error={
                        formik.touched.category &&
                        Boolean(formik.errors.category)
                      }
                      helperText={
                        formik.touched.category &&
                        typeof formik.errors.category === "string"
                          ? formik.errors.category
                          : undefined
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiInputBase-root": {
                          height: "32px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "6px 12px",
                          height: "20px",
                        },
                      }}
                    />
                  )}
                  ListboxProps={{
                    ...autocompleteDropdownStyles.ListboxProps,
                    style: {
                      maxHeight: "200px",
                      overflow: "auto",
                      zIndex: 100,
                    },
                  }}
                  componentsProps={{
                    popper: {
                      style: {
                        zIndex: 100,
                      },
                      modifiers: [
                        {
                          name: "preventOverflow",
                          enabled: true,
                        },
                      ],
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <Autocomplete
                  size="small"
                  options={subCategoryOptions || []}
                  autoHighlight
                  disabled={!formik?.values?.category}
                  value={formik?.values?.sub_category}
                  onChange={(_, val) => {
                    formik.setFieldValue("sub_category", val);
                    if (
                      val &&
                      formik.values.category &&
                      formik.values.category.category_id
                    ) {
                      fetchItemOptions(
                        formik.values.category.category_id,
                        val?.sub_category_id
                      );
                    
                    }

                    setItemOptions([]);
                    formik.setFieldValue("item", null);
                    formik.setFieldValue("item_code", "");
                    formik.setFieldValue("unit", "");
                    formik.setFieldValue("rate", "");
                    formik.setFieldValue("gst", null);
                    formik.setFieldValue("quantity", "");
                    formik.setFieldValue("bags", []);
                    setSelectedBags([]);
                    formik.setFieldValue("discount", "");
                    formik.setFieldValue("discount_type", "%");
                    formik.setFieldValue("discount_amount", "");
                    formik.setFieldValue("gst_amount", "");
                    formik.setFieldValue("total_amount", "");
                    formik.setFieldValue("remark", "");
                  }}
                  onBlur={() => formik.setFieldTouched("sub_category", true)}
                  getOptionLabel={(option) =>
                    option ? option?.sub_category_name : ""
                  }
                  renderOption={(props, option: any) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt?.sub_category_name || ""
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Sub Category"
                      error={
                        formik.touched.sub_category &&
                        Boolean(formik.errors.sub_category)
                      }
                      helperText={
                        formik.touched.sub_category &&
                        typeof formik.errors.sub_category === "string"
                          ? formik.errors.sub_category
                          : undefined
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiInputBase-root": {
                          height: "32px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "6px 12px",
                          height: "20px",
                        },
                      }}
                    />
                  )}
                  ListboxProps={{
                    ...autocompleteDropdownStyles.ListboxProps,
                    style: {
                      maxHeight: "200px",
                      overflow: "auto",
                      zIndex: 100,
                    },
                  }}
                  componentsProps={{
                    popper: {
                      style: {
                        zIndex: 100,
                      },
                      modifiers: [
                        {
                          name: "preventOverflow",
                          enabled: true,
                        },
                      ],
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <Autocomplete
                  size="small"
                  options={itemOptions || []}
                  autoHighlight
                  disabled={!formik?.values?.sub_category}
                  value={formik?.values?.item}
                  onChange={(_, val) => {
                    if (val) {
                      formik.setFieldValue("item", val);
                      formik.setFieldValue("item_code", val?.item_code);
                      formik.setFieldValue("unit", val?.unit?.unit_name);
                      formik.setFieldValue("gst", val?.hsn_code?.gst_percent);
                      formik.setFieldValue("rate", val?.selling_price);
                    } else {
                      formik.setFieldValue("item", val);
                      formik.setFieldValue("item_code", "");
                      formik.setFieldValue("unit", "");
                      formik.setFieldValue("rate", "");
                      formik.setFieldValue("gst", null);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("item", true)}
                  getOptionLabel={(option) => (option ? option?.item_name : "")}
                  renderOption={(props, option: any) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt?.item_name || ""
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Item Name"
                      error={formik.touched.item && Boolean(formik.errors.item)}
                      helperText={
                        formik.touched.item &&
                        typeof formik.errors.item === "string"
                          ? formik.errors.item
                          : undefined
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiInputBase-root": {
                          height: "32px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "6px 12px",
                          height: "20px",
                        },
                      }}
                    />
                  )}
                  ListboxProps={{
                    ...autocompleteDropdownStyles.ListboxProps,
                    style: {
                      maxHeight: "200px",
                      overflow: "auto",
                      zIndex: 100,
                    },
                  }}
                  componentsProps={{
                    popper: {
                      style: {
                        zIndex: 100,
                      },
                      modifiers: [
                        {
                          name: "preventOverflow",
                          enabled: true,
                        },
                      ],
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  value={formik.values.item_code || ""}
                  disabled
                  placeholder="Code"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  value={formik.values.unit || ""}
                  disabled
                  placeholder="Unit"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  type="text"
                  placeholder="Enter Quantity"
                  value={formik.values.quantity}
                  onKeyDown={(e) => {
                    if (
                      [
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                      ].includes(e.key)
                    ) {
                      return; // allow control keys
                    }
                    if (!/^[0-9]$/.test(e.key)) {
                      e.preventDefault(); // block all non-digits
                    }
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue("quantity", value);
                    // Clear bags when quantity changes
                    if (formik.values.bags && formik.values.bags.length > 0) {
                      // Remove bags from bagQuantityValidation
                      if (setBagQuantityValidation && bagQuantityValidation) {
                        const updatedBagValidation = [...bagQuantityValidation];
                        formik.values.bags.forEach((bag: any) => {
                          const bagIndex = updatedBagValidation.findIndex(
                            (b) =>
                              String(b.inventory_item_id) ===
                              String(bag.inventory_item_id)
                          );
                          if (bagIndex !== -1) {
                            updatedBagValidation[bagIndex].issue_quantity -=
                              parseFloat(String(bag.issue_quantity)) || 0;
                            // Remove if issue_quantity becomes 0 or negative
                            if (
                              updatedBagValidation[bagIndex].issue_quantity <= 0
                            ) {
                              updatedBagValidation.splice(bagIndex, 1);
                            }
                          }
                        });
                        setBagQuantityValidation(updatedBagValidation);
                      }
                      formik.setFieldValue("bags", []);
                      setSelectedBags([]);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={!!formik.errors.quantity && formik.touched.quantity}
                  helperText={
                    formik.touched.quantity &&
                    typeof formik.errors.quantity === "string"
                      ? formik.errors.quantity
                      : undefined
                  }
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={!formik?.values?.item || !warehouseId}
                    onClick={() => {
                      if (
                        !formik.values.quantity ||
                        parseFloat(formik.values.quantity) <= 0
                      ) {
                        toast.error("Please enter a valid quantity first");
                        return;
                      }
                      // Initialize selected bags from formik if they exist
                      // Add id field for modal internal use
                      if (formik.values.bags && formik.values.bags.length > 0) {
                        const bagsWithId = formik.values.bags.map(
                          (bag: any, idx: number) => ({
                            ...bag,
                            id: bag.id || `bag-${Date.now()}-${idx}`,
                          })
                        );
                        setSelectedBags(bagsWithId);
                      } else {
                        setSelectedBags([]);
                      }
                      setShowBagModal(true);
                    }}
                    style={{
                      height: 32,
                      minWidth: "100%",
                    }}
                  >
                    Select
                  </Button>
                  {formik.touched.bags && formik.errors.bags && (
                    <div
                      className="text-danger"
                      style={{ fontSize: "0.75rem", marginTop: "4px" }}
                    >
                      {typeof formik.errors.bags === "string"
                        ? formik.errors.bags
                        : "At least one bag is required"}
                    </div>
                  )}
                  {formik.values.bags && formik.values.bags.length > 0 && (
                    <div
                      className="text-success"
                      style={{ fontSize: "0.75rem", marginTop: "4px" }}
                    >
                      {formik.values.bags.length} bag(s) selected
                    </div>
                  )}
                </div>
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  type="text"
                  placeholder="Enter Rate"
                  value={formik.values.rate}
                  onKeyDown={(e) => {
                    if (
                      [
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                      ].includes(e.key)
                    ) {
                      return; // allow control keys
                    }
                    if (!/^[0-9]$/.test(e.key)) {
                      e.preventDefault(); // block all non-digits
                    }
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue("rate", value);
                  }}
                  onBlur={formik.handleBlur}
                  error={!!formik.errors.rate && formik.touched.rate}
                  helperText={
                    formik.touched.rate &&
                    typeof formik.errors.rate === "string"
                      ? formik.errors.rate
                      : undefined
                  }
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  value={formik.values.amount || ""}
                  disabled
                  placeholder="Auto Calculated"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "flex-start",
                  }}
                >
                  <TextField
                    size="small"
                    type="text"
                    placeholder="Enter Discount"
                    value={formik.values.discount}
                    // onChange={(e) => {
                    //     const value = e.target.value;
                    //     formik.setFieldValue("discount", value);
                    // }}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numberRegex = /^\d*\.?\d*$/;

                      if (value === "" || numberRegex.test(value)) {
                        formik.setFieldValue("discount", value);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    error={!!formik.errors.discount && formik.touched.discount}
                    helperText={
                      formik.touched.discount &&
                      typeof formik.errors.discount === "string"
                        ? formik.errors.discount
                        : undefined
                    }
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      flex: 1,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                  <Autocomplete
                    size="small"
                    options={["%", "₹"]}
                    autoHighlight
                    disableClearable
                    value={formik?.values?.discount_type}
                    onKeyDown={(e) => {
                      if (e.key === "-" || e.key === "+") {
                        e.preventDefault();
                      }
                    }}
                    onChange={(_, val) => {
                      formik.setFieldValue("discount_type", val);
                    }}
                    onBlur={() => formik.setFieldTouched("discount_type", true)}
                    getOptionLabel={(option) => (option ? option : "")}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt) => opt?.toString() || ""
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={
                          formik.touched.discount_type &&
                          Boolean(formik.errors.discount_type)
                        }
                        helperText={
                          formik.touched.discount_type &&
                          typeof formik.errors.discount_type === "string"
                            ? formik.errors.discount_type
                            : undefined
                        }
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                          width: "80px",
                          "& .MuiInputBase-root": {
                            height: "32px",
                            maxWidth: "60px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "6px 5px",
                            height: "20px",
                            maxWidth: "60px",
                          },
                        }}
                      />
                    )}
                    ListboxProps={{
                      ...autocompleteDropdownStyles.ListboxProps,
                      style: {
                        maxHeight: "200px",
                        overflow: "auto",
                        zIndex: 100,
                      },
                    }}
                    componentsProps={{
                      popper: {
                        style: {
                          zIndex: 100,
                        },
                        modifiers: [
                          {
                            name: "preventOverflow",
                            enabled: true,
                          },
                        ],
                      },
                    }}
                  />
                </div>
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  value={formik.values.discount_amount || ""}
                  disabled
                  placeholder="Auto Calculated"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <Autocomplete
                  size="small"
                  options={gstOptions || []}
                  autoHighlight
                  value={formik?.values?.gst}
                  onKeyDown={(e) => {
                    if (!/^[0-9]$/.test(e.key)) {
                      e.preventDefault(); // block all non-digits
                    }
                  }}
                  onChange={(_, val) => {
                    formik.setFieldValue("gst", val);
                  }}
                  onBlur={() => formik.setFieldTouched("gst", true)}
                  getOptionLabel={(option) =>
                    option && option?.gst_percent
                      ? String(option.gst_percent)
                      : ""
                  }
                  renderOption={(props, option: any) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => (opt?.gst_percent ? String(opt.gst_percent) : "")
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select GST"
                      error={formik.touched.gst && Boolean(formik.errors.gst)}
                      helperText={
                        formik.touched.gst &&
                        typeof formik.errors.gst === "string"
                          ? formik.errors.gst
                          : undefined
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiInputBase-root": {
                          height: "32px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "6px 12px",
                          height: "20px",
                        },
                      }}
                    />
                  )}
                  ListboxProps={{
                    ...autocompleteDropdownStyles.ListboxProps,
                    style: {
                      maxHeight: "200px",
                      overflow: "auto",
                      zIndex: 100,
                    },
                  }}
                  componentsProps={{
                    popper: {
                      style: {
                        zIndex: 100,
                      },
                      modifiers: [
                        {
                          name: "preventOverflow",
                          enabled: true,
                        },
                      ],
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  value={formik.values.gst_amount || ""}
                  disabled
                  placeholder="Auto Calculated"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  value={formik.values.total_amount || ""}
                  disabled
                  placeholder="Auto Calculated"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  placeholder="Enter Remark"
                  value={formik.values.remarks}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue("remarks", value);
                  }}
                  onBlur={formik.handleBlur}
                  error={!!formik.errors.remarks && formik.touched.remarks}
                  helperText={
                    formik.touched.remarks &&
                    typeof formik.errors.remarks === "string"
                      ? formik.errors.remarks
                      : undefined
                  }
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td className="sticky-column" style={tableStyles.actionCell}>
                <div style={tableStyles.actionButtonContainer}>
                  <SubmitButton
                    isSubmitting={false}
                    onClick={() =>
                      customSubmitHandler
                        ? customSubmitHandler()
                        : formik?.handleSubmit()
                    }
                  />
                </div>
              </td>
            </tr>

            {/* Existing Rows */}
            {mainFormik?.values?.items?.length === 0 ? (
              <tr>
                <td
                  colSpan={16}
                  style={{ ...tableStyles.cell, textAlign: "center" }}
                  className="py-2"
                >
                  No Items added. Please add at least one item.
                </td>
                <td
                  style={tableStyles.actionCell}
                  className="sticky-column"
                ></td>
              </tr>
            ) : (
              mainFormik?.values?.items?.map((row: any, idx: number) => {
                // Compare IDs using string conversion to handle type mismatches
                const rowId = String(row.id || row.quotation_item_id || "");
                const editingId = String(editingItemId || "");
                const isEditing = rowId === editingId && editingId !== "";
                const displayData =
                  isEditing && editingRowData ? editingRowData : row;

                return (
                  <tr
                    key={row.id || idx}
                    style={isEditing ? tableStyles.rowEditing : {}}
                  >
                    <td style={tableStyles.cell}>{idx + 1}</td>

                    {/* Category */}
                    <td style={tableStyles.cell}>
                      <span>{row?.category?.category_name || "-"}</span>
                    </td>

                    {/* Sub Category */}
                    <td style={tableStyles.cell}>
                      <span>{row?.sub_category?.sub_category_name || "-"}</span>
                    </td>

                    {/* Item Name */}
                    <td style={tableStyles.cell}>
                      <span>{row?.item?.item_name || "-"}</span>
                    </td>

                    {/* Item Code */}
                    <td style={tableStyles.cell}>
                      <span>{row?.item_code || "-"}</span>
                    </td>

                    {/* Unit */}
                    <td style={tableStyles.cell}>
                      <span>{row?.unit?.unit_name || row?.unit || "-"}</span>
                    </td>

                    {/* Quantity */}
                    <td style={tableStyles.cell}>
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="text"
                          value={displayData.quantity || ""}
                          onKeyDown={(e) => {
                            if (
                              [
                                "Backspace",
                                "Delete",
                                "ArrowLeft",
                                "ArrowRight",
                                "Tab",
                              ].includes(e.key)
                            ) {
                              return; // allow control keys
                            }
                            if (!/^[0-9]$/.test(e.key)) {
                              e.preventDefault(); // block all non-digits
                            }
                          }}
                          onChange={(e) =>
                            handleEditFieldChange("quantity", e.target.value)
                          }
                          fullWidth
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                            "& .MuiInputBase-root": {
                              height: "32px",
                            },
                            "& .MuiInputBase-input": {
                              padding: "6px 12px",
                              height: "20px",
                            },
                          }}
                        />
                      ) : (
                        <span>{row?.quantity || "-"}</span>
                      )}
                    </td>

                    {/* Bags */}
                    <td style={tableStyles.cell}>
                      {isEditing ? (
                        <div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              if (
                                !displayData.quantity ||
                                parseFloat(displayData.quantity) <= 0
                              ) {
                                toast.error(
                                  "Please enter a valid quantity first"
                                );
                                return;
                              }
                              // Don't set selectedBags when editing - modal will use editingRowData.bags
                              setShowBagModal(true);
                            }}
                            style={{
                              height: 32,
                              minWidth: "100%",
                            }}
                          >
                            Select
                          </Button>
                          {displayData.bags && displayData.bags.length > 0 && (
                            <div
                              className="text-success"
                              style={{ fontSize: "0.75rem", marginTop: "4px" }}
                            >
                              {displayData.bags.length} bag(s) selected
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ textAlign: "center" }}>
                          {row?.bags && row.bags.length > 0 ? (
                            <VisibilityOutlinedIcon
                              style={{
                                color: Colors?.bg_blue,
                                cursor: "pointer",
                                fontSize: "1.5rem",
                              }}
                              onClick={() => {
                                setViewBagsForItem(row.bags);
                                setShowViewBagsModal(true);
                              }}
                            />
                          ) : (
                            "-"
                          )}
                        </div>
                      )}
                    </td>

                    {/* Rate */}
                    <td style={tableStyles.cell}>
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="text"
                          placeholder="Enter Rate"
                          value={displayData.rate || ""}
                          onKeyDown={(e) => {
                            if (
                              [
                                "Backspace",
                                "Delete",
                                "ArrowLeft",
                                "ArrowRight",
                                "Tab",
                              ].includes(e.key)
                            ) {
                              return; // allow control keys
                            }
                            if (!/^[0-9]$/.test(e.key)) {
                              e.preventDefault(); // block all non-digits
                            }
                          }}
                          onChange={(e) =>
                            handleEditFieldChange("rate", e.target.value)
                          }
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                            "& .MuiInputBase-root": {
                              height: "32px",
                            },
                            "& .MuiInputBase-input": {
                              padding: "6px 12px",
                              height: "20px",
                            },
                          }}
                        />
                      ) : (
                        <span>{row?.rate || "-"}</span>
                      )}
                    </td>

                    {/* Amount */}
                    <td style={tableStyles.cell}>
                      <span>{displayData.amount || "-"}</span>
                    </td>

                    {/* Discount */}
                    <td style={tableStyles.cell}>
                      {isEditing ? (
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "flex-start",
                          }}
                        >
                          <TextField
                            size="small"
                            type="text"
                            value={displayData.discount || ""}
                            // onChange={(e) =>
                            //     handleEditFieldChange("discount", e.target.value)
                            // }
                            onChange={(e) => {
                              const value = e.target.value;
                              const numberRegex = /^\d*\.?\d*$/;

                              if (value === "" || numberRegex.test(value)) {
                                handleEditFieldChange("discount", value);
                              }
                            }}
                            sx={{
                              ...autocompleteDropdownStyles.inputSx,
                              flex: 1,
                              "& .MuiInputBase-root": {
                                height: "32px",
                              },
                              "& .MuiInputBase-input": {
                                padding: "6px 12px",
                                height: "20px",
                              },
                            }}
                          />
                          <Autocomplete
                            size="small"
                            options={["%", "₹"]}
                            disableClearable
                            value={displayData.discount_type || "%"}
                            onChange={(_, value) =>
                              handleEditFieldChange("discount_type", value)
                            }
                            renderOption={(props, option) =>
                              autocompleteDropdownStyles.renderOption(
                                props,
                                option,
                                (opt) => opt?.toString() || ""
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                sx={{
                                  ...autocompleteDropdownStyles.inputSx,
                                  width: "80px",
                                  "& .MuiInputBase-root": {
                                    height: "32px",
                                  },
                                  "& .MuiInputBase-input": {
                                    padding: "6px 12px",
                                    height: "20px",
                                  },
                                }}
                              />
                            )}
                            ListboxProps={{
                              ...autocompleteDropdownStyles.ListboxProps,
                              style: {
                                maxHeight: "200px",
                                overflow: "auto",
                                zIndex: 100,
                              },
                            }}
                            componentsProps={{
                              popper: {
                                style: {
                                  zIndex: 100,
                                },
                                modifiers: [
                                  {
                                    name: "preventOverflow",
                                    enabled: true,
                                  },
                                ],
                              },
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          {row?.discount &&
                          parseFloat(String(row.discount)) > 0 ? (
                            <>
                              <span>{row.discount}</span>
                              <span>{row?.discount_type || "-"}</span>
                            </>
                          ) : (
                            <span>-</span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Discount Amount */}
                    <td style={tableStyles.cell}>
                      <span>
                        {displayData.discount_amount &&
                        parseFloat(String(displayData.discount_amount)) > 0
                          ? displayData.discount_amount
                          : "-"}
                      </span>
                    </td>

                    {/* GST */}
                    <td style={tableStyles.cell}>
                      <span>{row?.gst?.gst_percent || "-"}</span>
                    </td>

                    {/* GST Amount */}
                    <td style={tableStyles.cell}>
                      <span>{displayData.gst_amount || "-"}</span>
                    </td>

                    {/* Total Amount */}
                    <td style={tableStyles.cell}>
                      <span>{displayData.total_amount || "-"}</span>
                    </td>

                    {/* Remarks */}
                    <td style={tableStyles.cell}>
                      {isEditing ? (
                        <TextField
                          size="small"
                          placeholder="Enter Remark"
                          value={displayData.remarks || ""}
                          onChange={(e) =>
                            handleEditFieldChange("remarks", e.target.value)
                          }
                          fullWidth
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                            "& .MuiInputBase-root": {
                              height: "32px",
                            },
                            "& .MuiInputBase-input": {
                              padding: "6px 12px",
                              height: "20px",
                            },
                          }}
                        />
                      ) : (
                        <TruncatedTextCell
                          text={row?.remarks}
                          maxWidth="300px"
                          maxLines={4}
                          rowIndex={idx}
                          fallbackText="—"
                        />
                      )}
                    </td>

                    {/* Actions */}
                    <td
                      style={
                        isEditing
                          ? tableStyles.actionCellEditing
                          : tableStyles.actionCell
                      }
                      className="sticky-column"
                    >
                      {isEditing ? (
                        <div style={tableStyles.actionButtonContainer}>
                          <SaveCancelButtons
                            onSave={handleSaveEdit}
                            onCancel={handleCancelEdit}
                          />
                        </div>
                      ) : (
                        <div style={tableStyles.actionButtonContainer}>
                          <EditButton
                            onClick={() => handleEditItem(row)}
                            iconClassName="ph-pencil"
                          />
                          <DeleteButton
                            onClick={() => {
                              const currentItems =
                                mainFormik.values.items || [];
                              const rowId = String(
                                row.id || row.quotation_item_id || ""
                              );
                              const itemToDelete = currentItems.find(
                                (item: any) => {
                                  const itemId = String(
                                    item.id || item.quotation_item_id || ""
                                  );
                                  return itemId === rowId;
                                }
                              );

                              // Remove bags from bagQuantityValidation when deleting item
                              if (
                                itemToDelete?.bags &&
                                itemToDelete.bags.length > 0 &&
                                setBagQuantityValidation &&
                                bagQuantityValidation
                              ) {
                                const updatedBagValidation = [
                                  ...bagQuantityValidation,
                                ];
                                itemToDelete.bags.forEach((bag: any) => {
                                  const bagIndex =
                                    updatedBagValidation.findIndex(
                                      (b) =>
                                        String(b.inventory_item_id) ===
                                        String(bag.inventory_item_id)
                                    );
                                  if (bagIndex !== -1) {
                                    updatedBagValidation[
                                      bagIndex
                                    ].issue_quantity -=
                                      parseFloat(String(bag.issue_quantity)) ||
                                      0;
                                    // Remove if issue_quantity becomes 0 or negative
                                    if (
                                      updatedBagValidation[bagIndex]
                                        .issue_quantity <= 0
                                    ) {
                                      updatedBagValidation.splice(bagIndex, 1);
                                    }
                                  }
                                });
                                setBagQuantityValidation(updatedBagValidation);
                              }

                              const updatedItems = currentItems.filter(
                                (item: any) => {
                                  const itemId = String(
                                    item.id || item.quotation_item_id || ""
                                  );
                                  return itemId !== rowId;
                                }
                              );
                              mainFormik.setFieldValue("items", updatedItems);

                              // If deleting the item being edited, cancel edit mode
                              const editingId = String(editingItemId || "");
                              if (editingId === rowId && setEditingItemId) {
                                setEditingItemId(null);
                                setEditingRowData(null);
                                formik.resetForm();
                                setSelectedBags([]);
                              }
                            }}
                            iconClassName="ph-trash"
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
      <QuotationBagSelectionModal
        show={showBagModal}
        tableFormik={formik}
        handleClose={() => setShowBagModal(false)}
        quantity={
          editingItemId !== null && editingRowData
            ? parseFloat(editingRowData.quantity) || 0
            : parseFloat(formik.values.quantity) || 0
        }
        onSave={(bags) => {
          // If editing an item, only update editingRowData, not the add form
          if (editingItemId !== null && editingRowData) {
            setEditingRowData({ ...editingRowData, bags });
          } else {
            // Only update add form when not editing
            setSelectedBags(bags);
            formik.setFieldValue("bags", bags);
          }
          setShowBagModal(false);
        }}
        initialBags={
          editingItemId !== null && editingRowData
            ? editingRowData.bags || []
            : selectedBags
        }
        bagQuantityValidation={bagQuantityValidation || []}
        setBagQuantityValidation={setBagQuantityValidation || (() => {})}
        editingItemId={editingItemId}
        currentItemBags={
          editingItemId !== null
            ? mainFormik?.values?.items?.find((item: any) => {
                const itemId = String(item.id || item.quotation_item_id || "");
                const editingId = String(editingItemId || "");
                return itemId === editingId;
              })?.bags || []
            : []
        }
        editingRowData={editingRowData}
        warehouseId={warehouseId}
      />
      <QuotationViewBagsModal
        show={showViewBagsModal}
        handleClose={() => setShowViewBagsModal(false)}
        bags={viewBagsForItem}
      />
    </>
  );
};

export default QuotationItemTable;
